import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Use relative URL for API calls - works for both preview and production
const API = "/api";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState("");

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      const adminToken = localStorage.getItem("admin_token");
      
      // Check admin/team member token first
      if (adminToken) {
        const role = localStorage.getItem("admin_role");
        if (role === "product_manager") {
          navigate("/admin/crm", { replace: true });
        } else if (role === "admin") {
          navigate("/admin", { replace: true });
        }
        setCheckingAuth(false);
        return;
      }
      
      // Check regular user token
      if (token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const userData = await response.json();
            if (userData.is_admin) {
              // Get admin token for admin panel access
              const adminTokenRes = await fetch(`${API}/auth/admin-token`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
              });
              if (adminTokenRes.ok) {
                const adminData = await adminTokenRes.json();
                localStorage.setItem("admin_token", adminData.admin_token);
                localStorage.setItem("admin_email", adminData.admin_email);
                localStorage.setItem("admin_role", adminData.role || "admin");
                localStorage.setItem("admin_name", adminData.name || "");
              }
              navigate("/admin", { replace: true });
            } else {
              navigate("/chat", { replace: true });
            }
            return;
          }
        } catch (error) {
          localStorage.removeItem("auth_token");
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, [navigate]);

  // Unified login handler - tries all auth methods
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    setLoginError("");
    
    try {
      // First, try admin/team member login
      const adminResponse = await fetch(`${API}/admin/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      if (adminResponse.ok) {
        const data = await adminResponse.json();
        
        // Store admin/team member session
        localStorage.setItem("admin_token", data.access_token);
        localStorage.setItem("admin_email", data.admin_email);
        localStorage.setItem("admin_role", data.role || "admin");
        localStorage.setItem("admin_name", data.name || "");
        if (data.team_member_id) {
          localStorage.setItem("admin_team_member_id", data.team_member_id);
        }
        
        toast.success(`Welcome back${data.name ? ', ' + data.name : ''}!`);
        
        // Route based on role
        if (data.role === "product_manager") {
          navigate("/admin/crm", { replace: true });
        } else {
          navigate("/admin", { replace: true });
        }
        return;
      }
      
      // If not admin/team, try regular user login
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();

      if (!response.ok) {
        setLoginError("Incorrect email or password. Please check your credentials and try again.");
        toast.error("Login failed - check your email and password", { duration: 5000 });
        setLoading(false);
        return;
      }

      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("user", JSON.stringify({ 
        email: data.user?.email || email,
        name: data.user?.name || "",
        is_admin: data.is_admin || false 
      }));
      toast.success("Welcome back!");
      
      // Redirect admins to admin panel, regular users to chat
      if (data.is_admin) {
        try {
          const adminTokenRes = await fetch(`${API}/auth/admin-token`, {
            method: "POST",
            headers: { Authorization: `Bearer ${data.access_token}` },
          });
          if (adminTokenRes.ok) {
            const adminData = await adminTokenRes.json();
            localStorage.setItem("admin_token", adminData.admin_token);
            localStorage.setItem("admin_email", adminData.admin_email || email);
            localStorage.setItem("admin_role", adminData.role || "admin");
            localStorage.setItem("admin_name", adminData.name || "");
          }
        } catch (adminErr) {
          console.error("Failed to get admin token:", adminErr);
        }
        navigate("/admin", { replace: true });
      } else {
        navigate("/chat", { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error?.name === 'AbortError') {
        toast.error("Request timed out. Please check your internet connection.");
      } else {
        toast.error("Unable to connect to server. Please try again.");
      }
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#14A800]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Welcome back
          </h1>
          <p className="text-[#888888]">Log in to your account</p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-5">
          {/* Error Alert */}
          {loginError && (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-start gap-3" data-testid="login-error-alert">
              <div className="bg-red-500 rounded-full p-1 mt-0.5">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="text-red-400 font-semibold">Login Failed</p>
                <p className="text-red-300 text-sm mt-1">{loginError}</p>
              </div>
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white font-medium">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setLoginError(""); }}
              placeholder="Enter your email"
              className={`h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg ${loginError ? 'border-red-500' : ''}`}
              data-testid="email-input"
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-white font-medium">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
                placeholder="Enter your password"
                className={`h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg pr-12 ${loginError ? 'border-red-500' : ''}`}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link to="/forgot-password" className="text-[#14A800] hover:text-[#12B000] text-sm font-medium">
              Forgot Password?
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#14A800] hover:bg-[#128A00] text-white font-semibold rounded-lg text-base"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Signing in...
              </>
            ) : (
              "Log In"
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#333333]"></div>
          </div>
        </div>

        {/* Sign Up Link */}
        <p className="text-center text-[#888888]">
          Don't have an account?{" "}
          <Link to="/signup" className="text-[#14A800] hover:text-[#12B000] font-semibold">
            Get started
          </Link>
        </p>
      </div>
    </div>
  );
}
